import { Cog6ToothIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  getSmartPredictMessages,
  getSmartPredictStates
} from '../../constants/smart-predict'
import trackEvents from '../../constants/track-events'
import {
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { mutation, query } from '../../graphql'
import { classNames } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { ROLES } from '../../helpers/authorization-roles'
import {
  STATUS_COLORS,
  buildTalentStatusMetaEntry,
  readTalentStatusMeta,
  resolveLocalized,
  toStatusOption,
  withTalentStatusMeta
} from '../../helpers/talent-statuses'
import { useCreateLog } from '../../hooks/use-create-log'
import Alert from '../alert'
import RadioGroup from '../radio-group'
import RadioInputGroup from '../radio-input-group'
import Button from '../tailwind/Button'
import GroupLabel from '../tailwind/GroupLabel'
import InfoCallout from '../tailwind/InfoCallout'
import SlideoverWithTitleAndActions from '../tailwind/slideover-with-title-and-actions'
import Toggle from '../tailwind/toggle'

const TRANSLATION_LOCATION = 'components.career.set_space_state.'
const TALENT_STATUS_SETTINGS_PATH = '/settings/talent-status'

// Die 5 Aivy-Status, die die helperControl-Lambda in application_status schreibt.
const SYSTEM_STATUS_IDS = getSmartPredictStates().map(({ id }) => id)

// Zuletzt gesetzter Status eines einzelnen Talents für die Vorauswahl. Auf der
// Detailseite aus den Logs (inkl. Passungs-Option), in der Tabelle gibt es
// keine Logs, dort reicht der abgeleitete Talent-Status.
const getLastApplicantStatus = (spaces) => {
  if (spaces.length !== 1) return null

  const [space] = spaces

  if (Array.isArray(space.logs)) {
    const last = space.logs
      .map((log) => ({ ...log, data: JSON.parse(log.data) }))
      .filter(({ data }) => data.context === 'APPLICANT_STATUS')
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .pop() // .filter creates a new array

    if (last) return last.data
  }

  if (space.smart_predict_status?.isUserStatus) {
    return { status: space.smart_predict_status.status.id }
  }

  return null
}

const SetSpaceState = ({
  spaces,
  updateHandler,
  open,
  setOpen,
  setIsCustomerEffortScoreVisible
}) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { success } = useNotificationContext()
  const {
    activeSpace,
    cognitoUser: { username }
  } = useUserContext()
  const { partner } = usePartnerContext()

  const navigate = useNavigate()

  const { createSpaceHistoryLog } = useCreateLog()

  const hasAdminRole = activeSpace.authorization_role.id === ROLES.ADMIN.id

  const [isLoading, setIsLoading] = useState(false)
  const [sendMailEnabled, setSendMailEnabled] = useState(false)

  const systemStates = useMemo(
    () =>
      getSmartPredictStates().map((state) => ({
        ...state,
        label: t(state.label),
        description: state.description ? t(state.description) : undefined
      })),
    [t]
  )

  // Eigene Status des Partners, nur aktive sind wählbar. labelMap bleibt als
  // Snapshot für Meta-Eintrag und Log erhalten.
  const customStates = useMemo(
    () =>
      (partner.talentStatuses || [])
        .filter((definition) => !definition.archived)
        .map((definition, index) => {
          const option = toStatusOption(definition, index)

          return {
            ...option,
            labelMap: definition.label,
            label: (
              <span className='inline-flex items-center gap-x-2'>
                <span
                  aria-hidden='true'
                  className={classNames(
                    'size-2.5 rounded-full',
                    STATUS_COLORS[option.color]?.dot
                  )}
                />
                {resolveLocalized(definition.label, language)}
              </span>
            ),
            description: resolveLocalized(definition.description, language)
          }
        }),
    [partner.talentStatuses, language]
  )

  const spaceStateMessages = useMemo(
    () =>
      getSmartPredictMessages().map((option) => ({
        ...option,
        label: t(option.label),
        description: t(option.description)
      })),
    [t]
  )
  const defaultMessageId = spaceStateMessages.find(
    (option) => option.default
  ).id

  const [selectedSpaceState, setSelectedSpaceState] = useState(null)
  const [selectedSpaceStateMessage, setSelectedSpaceStateMessage] =
    useState(defaultMessageId)

  // spaces ist auf /talents bei jedem Render ein neues Array. Über den Ref
  // hängt das Öffnen-Reset nur an open, ohne die Dependency-Regel zu umgehen.
  const spacesRef = useRef(spaces)
  spacesRef.current = spaces

  // Beim Öffnen aus den aktuellen Talenten neu befüllen. Die Instanzen auf
  // /talents bleiben gemountet, useState-Initialwerte greifen dort nicht, und
  // eine alte Auswahl darf nicht ins nächste Öffnen leaken.
  useEffect(() => {
    if (!open) return

    const currentSpaces = spacesRef.current
    const last = getLastApplicantStatus(currentSpaces)

    setSelectedSpaceState(last?.status || null)
    setSelectedSpaceStateMessage(last?.message || defaultMessageId)
    setSendMailEnabled(false)

    trackEvent(trackEvents.OPEN_APPLICANT_STATUS, {
      count: currentSpaces.length
    })
  }, [open, defaultMessageId])

  const selectedState =
    systemStates.find(({ id }) => id === selectedSpaceState) ||
    customStates.find(({ id }) => id === selectedSpaceState)

  const { displayOptions, displaySendMail } = selectedState || {}

  // Der Toggle wird nur bei einem Status mit displaySendMail gerendert. Wechselt
  // die Auswahl danach, verschwindet das Bedienelement, der State aber bleibt.
  // Ohne diesen Reset stünde der Versand beim nächsten Speichern noch scharf.
  useEffect(() => {
    if (!displaySendMail) setSendMailEnabled(false)
  }, [displaySendMail])

  const goToStatusSettings = () => {
    setOpen(false)
    navigate(TALENT_STATUS_SETTINGS_PATH)
  }

  const handleSave = async () => {
    if (!selectedState) return

    setIsLoading(true)

    const isCustom = !!selectedState.isCustom
    const spaceStateOption = spaceStateMessages.find(
      ({ id }) => id === selectedSpaceStateMessage
    )

    // Eine Bedingung für Verlaufseintrag und Versand. Vorher prüfte nur der
    // Eintrag den Status, der Versandblock allein den Toggle. Bei einem Wechsel
    // von Abgelehnt auf einen anderen Status ging die Absage-Mail dadurch raus,
    // ohne dass der Verlauf sie auswies. Das Template unten ist fest 'reject'.
    const sendRejectionMail = selectedState.id === 'REJECTED' && sendMailEnabled
    const now = new Date().toISOString()

    try {
      for (const space of spaces) {
        const { id, career_id, email } = space

        // Frisch laden: die Listen-Kopie kann Minuten alt sein und das
        // Meta-Array wird komplett zurückgeschrieben.
        const fresh = await query({ query: 'getSpace', variables: { id } })
        const currentMeta = readTalentStatusMeta(fresh.customer_dashboard_meta)

        // Bei unvollständigem Assessment den Assessment-Stand sichern, weil
        // die Lambda application_status mit dem Aivy-Status überschreibt.
        // Steht dort schon ein Aivy-Status, den früher gesicherten Wert behalten.
        let application_status_before
        if (fresh.progress !== 100) {
          application_status_before = SYSTEM_STATUS_IDS.includes(
            fresh.application_status
          )
            ? currentMeta?.application_status_before
            : fresh.application_status
        }

        const entry = buildTalentStatusMetaEntry({
          status: selectedState.id,
          label: isCustom ? selectedState.labelMap : undefined,
          application_status_before,
          fired_by_id: username,
          now
        })

        const input = {
          id,
          customer_dashboard_meta: withTalentStatusMeta(
            fresh.customer_dashboard_meta,
            entry
          )
        }

        if (selectedState.id === 'HIRED' || selectedState.id === 'REJECTED') {
          input.application_status = selectedState.id
        }

        const event = {
          context: 'APPLICANT_STATUS',
          status: selectedState.id,
          option: sendRejectionMail && !!email ? 'VIA_EMAIL' : undefined,
          message: selectedState.displayOptions
            ? spaceStateOption.id
            : undefined,
          label: isCustom ? selectedState.labelMap : undefined
        }

        await mutation({ mutation: 'updateSpace', input })
        await createSpaceHistoryLog({ space_id: id, career_id, ...event })
      }

      if (sendRejectionMail) {
        // space.email // comes from src/helpers/spaces-table-data.js
        const spaces_with_email = spaces.filter((space) => !!space.email)

        await Promise.all(
          spaces_with_email.map(
            ({ id: space_id, career_id, partner_id, email, language }) => {
              return query({
                query: 'inviteTalent',
                variables: {
                  space_id,
                  career_id,
                  partner_id,
                  email,
                  language,
                  email_template: 'reject'
                }
              })
            }
          )
        )
      }
    } catch (err) {
      setIsLoading(false)
      return
    }

    trackEvent(trackEvents.UPDATE_APPLICANT_STATUS, {
      status_type: isCustom ? 'custom' : 'system',
      count: spaces.length
    })

    success(t(TRANSLATION_LOCATION + 'update_space_success'))
    setIsLoading(false)
    setOpen(false)
    updateHandler()

    const FEEDBACK_ID = `${activeSpace.id}-CUSTOMER-EFFORT-SCORE`
    const ces = await query({
      query: 'getFeedback',
      variables: { id: FEEDBACK_ID },
      authMode: 'AWS_IAM'
    })

    if (ces === null) {
      setTimeout(() => {
        setIsCustomerEffortScoreVisible(true)
      }, 400)
    }
  }

  // space.email // comes from src/helpers/spaces-table-data.js
  const spaces_with_email = spaces.filter((space) => !!space.email)

  return (
    <SlideoverWithTitleAndActions
      open={open}
      setOpen={() => {
        setOpen(false)
        trackEvent(trackEvents.ABORT_APPLICANT_STATUS)
      }}
      title={t(TRANSLATION_LOCATION + 'popup_title')}
      description={t(TRANSLATION_LOCATION + 'popup_description', {
        number: spaces.length
      })}
      actions={
        <div className='flex flex-shrink-0 justify-end space-x-2 px-4 py-4'>
          <Button.SecondaryXL
            text={t(TRANSLATION_LOCATION + 'popup_cancel_action')}
            onClick={() => {
              setOpen(false)
              trackEvent(trackEvents.ABORT_APPLICANT_STATUS)
            }}
          />
          <Button.PrimaryXL
            text={t(
              TRANSLATION_LOCATION +
                (sendMailEnabled
                  ? 'popup_confirm_with_email_action'
                  : 'popup_confirm_action')
            )}
            onClick={handleSave}
            isLoading={isLoading}
            disabled={!selectedState}
          />
        </div>
      }
    >
      <div className='px-8 py-4'>
        <div className='mt-4'>
          <h3 className='text-sm font-semibold text-gray-900'>
            {t(TRANSLATION_LOCATION + 'states_group_label')}
          </h3>
          <div className='mt-4 pl-2'>
            <GroupLabel as='p' className='mb-3'>
              {t(TRANSLATION_LOCATION + 'system_group_label')}
            </GroupLabel>
            <RadioInputGroup
              name='talent-status'
              group={systemStates}
              selected={selectedSpaceState}
              setSelected={setSelectedSpaceState}
            />

            <GroupLabel as='p' className='mb-3 mt-8'>
              {t(TRANSLATION_LOCATION + 'custom_group_label')}
            </GroupLabel>
            {customStates.length > 0 ? (
              <RadioInputGroup
                name='talent-status'
                group={customStates}
                selected={selectedSpaceState}
                setSelected={setSelectedSpaceState}
              />
            ) : hasAdminRole ? (
              <InfoCallout
                action={
                  <button
                    type='button'
                    onClick={goToStatusSettings}
                    className='whitespace-nowrap rounded font-medium text-primaryBlue hover:text-primaryBlue/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue'
                  >
                    {t(TRANSLATION_LOCATION + 'custom_empty_admin_action')}
                  </button>
                }
              >
                {t(TRANSLATION_LOCATION + 'custom_empty_admin_hint')}
              </InfoCallout>
            ) : (
              <p className='text-sm text-gray-500'>
                {t(TRANSLATION_LOCATION + 'custom_empty_hint')}
              </p>
            )}

            {hasAdminRole && customStates.length > 0 && (
              <button
                type='button'
                onClick={goToStatusSettings}
                className='mt-4 inline-flex items-center gap-x-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-600'
              >
                <Cog6ToothIcon className='size-4' aria-hidden='true' />
                {t(TRANSLATION_LOCATION + 'manage_custom_action')}
              </button>
            )}
          </div>
        </div>
        {displayOptions && (
          <>
            <div className='mt-6 h-px w-full bg-gray-200' />
            <div className='mt-6'>
              <h3 className='text-sm font-semibold text-gray-900'>
                {t(TRANSLATION_LOCATION + 'options_group_label')}
              </h3>
              <div className='mt-4 px-2'>
                <RadioGroup
                  group={spaceStateMessages}
                  selected={selectedSpaceStateMessage}
                  setSelected={setSelectedSpaceStateMessage}
                />
              </div>
            </div>
            {displaySendMail && spaces_with_email.length > 0 && (
              <>
                <div className='mt-6 h-px w-full bg-gray-200' />
                <div className='mt-6'>
                  <Toggle
                    label={t(
                      TRANSLATION_LOCATION +
                        (spaces.length === 1
                          ? 'send_email_toggle_label'
                          : 'send_emails_toggle_label'),
                      { number: spaces_with_email.length }
                    )}
                    enabled={sendMailEnabled}
                    setEnabled={setSendMailEnabled}
                  />
                </div>
                {spaces.length === 1 && (
                  <div
                    className={classNames(
                      'mb-8 mt-4',
                      sendMailEnabled ? 'cursor-pointer' : 'opacity-40'
                    )}
                    onClick={
                      sendMailEnabled
                        ? () => {
                            if (spaces[0].career_id) {
                              navigate(
                                `/career/${spaces[0].career_id}#assessment-settings`
                              )
                            } else {
                              navigate('/settings/templates')
                            }
                          }
                        : undefined
                    }
                  >
                    <div className='mt-1 text-sm font-semibold leading-6 text-blue-600 hover:text-blue-500'>
                      {t(TRANSLATION_LOCATION + 'edit_template_action')}
                      <span aria-hidden='true'> &rarr;</span>
                    </div>
                  </div>
                )}
                {spaces.length > 1 && sendMailEnabled && (
                  <div className='mb-8 mt-4'>
                    <Alert
                      type='info'
                      title={t(
                        TRANSLATION_LOCATION + 'send_emails_alert_title'
                      )}
                      description={t(
                        TRANSLATION_LOCATION + 'send_emails_alert_description'
                      )}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </SlideoverWithTitleAndActions>
  )
}

SetSpaceState.propTypes = {
  spaces: PropTypes.array.isRequired,
  updateHandler: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  setIsCustomerEffortScoreVisible: PropTypes.func.isRequired
}

export default SetSpaceState
